import { Notice, Text } from '@genial/components';
import { Config } from '@genial/config';
import { Color } from '@genial/design-tokens';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { Box, Center, Pressable } from 'native-base';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import { useRecoilValue } from 'recoil';

import { angularFrameRoutesParams } from '../../routes/constants';
import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../routes/types';
import {
  masterStateSelectedAdvisorGroupSelector,
  masterStateSelectedAdvisorNameSelector,
  masterStateSelectedAdvisorSelector,
  masterStateSelectedPartnerSelector,
} from '../../store/master/selector';
import { userPermissions } from '../../store/permission/atom';
import { sessionState } from '../../store/session/atom';

const styles = StyleSheet.create({
  frame: {
    flex: 1,
    borderWidth: 0,
  },
});

export const AngularFrame = () => {
  const selectedAdvisor = useRecoilValue(masterStateSelectedAdvisorSelector);
  const selectedAdvisorName = useRecoilValue(masterStateSelectedAdvisorNameSelector);
  const selectedAdvisorGroup = useRecoilValue(masterStateSelectedAdvisorGroupSelector);
  const selectedPartner = useRecoilValue(masterStateSelectedPartnerSelector);
  const { token, userProfile } = useRecoilValue(sessionState);
  const [tokenSent, setTokenSent] = useState(false);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const route = useRoute<RouteProp<SignedInRootStackParamList, 'AngularFrame'>>();
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { compliance, consolidatedPortfolio, partners, functionalities, unicredBrb, trading } =
    useRecoilValue(userPermissions);

  const { state, ...extraParams } = route.params;

  const iframeKey = `iframe-${selectedAdvisor}`;
  const navigateToHome = useCallback(() => {
    navigate('AppDrawer', {
      screen: 'Home',
    });
  }, [navigate]);

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.data?.message === 'genialFrame:waitingForToken') {
        const message = {
          message: 'genialFrame:setToken',
          jwtToken: token,
          userData: userProfile,
          userName: userProfile?.email,
          impersonateId: selectedAdvisor,
          impersonateName: selectedAdvisorName,
          impersonateGroup: selectedAdvisorGroup,
          impersonatePartner: selectedPartner,
          firebasePermissions: {
            compliance: compliance ?? [],
            consolidatedPortfolio: consolidatedPortfolio ?? false,
            partners: partners ?? [],
            functionalities: functionalities ?? [],
            unicredBrb: unicredBrb ?? [],
            trading: trading ?? [],
          },
          state,
          extraParams,
        };

        frameRef.current!.contentWindow!.postMessage(message, Config.GenialFrameUrl);

        setTokenSent(true);
      }
    };

    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state,
    selectedAdvisorGroup,
    selectedAdvisor,
    selectedAdvisorName,
    selectedPartner,
    compliance,
    consolidatedPortfolio,
    partners,
    unicredBrb,
    trading,
  ]);

  useEffect(() => {
    if (!tokenSent) {
      return;
    }

    frameRef.current!.contentWindow!.postMessage(
      {
        message: 'genialFrame:tokenRefreshed',
        jwtToken: token,
      },
      Config.GenialFrameUrl
    );
  }, [token, tokenSent]);

  const legacyHomeAlert = useMemo(() => {
    if (
      [
        angularFrameRoutesParams.AngularFrame_Home.state,
        angularFrameRoutesParams.AngularFrame_Dashboard.state,
      ].includes(state)
    ) {
      return (
        <Center>
          <Notice
            textObjects={[
              { text: 'Atualização disponível! ', variant: 'bold' },
              {
                text: 'Esta tela será desativada no dia 19/01/25 e será substituída por uma nova versão com dados mais precisos e atualizados. ',
                variant: 'regular',
              },
              {
                text: (
                  <>
                    <Pressable onPress={navigateToHome}>
                      <Text underline color="brand.secondary.dark">
                        Acesse aqui a nova página
                      </Text>
                    </Pressable>
                    &nbsp;para conferir as melhorias e aproveitar uma experiência ainda melhor!
                  </>
                ),
                variant: 'regular',
              },
            ]}
            type="warning"
          />
        </Center>
      );
    }
  }, [navigateToHome, state]);

  return (
    <Box flex={1} backgroundColor={Color['neutral.white']}>
      {legacyHomeAlert}
      <iframe key={iframeKey} ref={frameRef} style={styles.frame} src={Config.GenialFrameUrl} />
    </Box>
  );
};
